import { useCallback, useMemo, useState } from "react";
import useEntity from "./entity";
import usePagination from "./pagination";

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function mergeSettings(baseSetting, newSettings) {
  //done
  const mergedSettings = { ...baseSetting, ...newSettings };

  //lets check about objects that should also be merged
  for (let key in baseSetting) {
    //const setting = baseSetting[key];
    if (typeof baseSetting[key] === "object") {
      mergedSettings[key] = mergeSettings(baseSetting[key], newSettings[key]);
    }
  }

  return mergedSettings;
}

const hook = (entityName, defaultSettings = {}, V2, alias, suffix) => {
  const [getData, setGetData] = useState([]);

  const {
    data,
    fetching,
    fetchingError,
    fetchData,
    getting,
    gettingError,
    detailGetData,
    patching,
    patchingError,
    patchData,
    putting,
    puttingError,
    putData,
    deleting,
    deletingError,
    deleteData,
    disabling,
    disablingError,
    disableData,
    enabling,
    enablingError,
    enableData
  } = useEntity(entityName, alias);

  const {
    currentPageData,
    setCurrentPageData,
    currentPage,
    setCurrentPage,
    rowCount,
    setRowCount,
    pageCount,
    setPageCount,
    pageSize,
    setPageSize
  } = usePagination();

  const URL_BASE = useMemo(
    () => `${V2 || ""}/${entityName}/${suffix || ""}`,
    []
  );

  const list = useCallback(
    async (settings = {}) => {
      const data = await fetchData(
        `${URL_BASE}${settings.id || ""}`,
        mergeSettings(defaultSettings, settings)
      );

      if (data.meta) {
        const { page, pageSize, rowCount, pageCount } = data.meta;
        setPageCount(pageCount);
        setRowCount(rowCount);
        setCurrentPage(page);
        setPageSize(pageSize);
        setCurrentPageData(
          data.data.map(instance =>
            instance.id ? instance.id : instance[alias]
          )
        );
      }
      return data;
    },
    [setPageCount, setRowCount, setCurrentPageData, setCurrentPage, setPageSize]
  );

  const get = useCallback(async (id, settings = {}) => {
    const data = await detailGetData(
      `${URL_BASE}${id}`,
      mergeSettings(defaultSettings, settings),
      id
    );
    setGetData(data);
    return data;
  }, []);

  const getByKey = useCallback(async (key, settings = {}) => {
    const data = await detailGetData(
      `${URL_BASE}key/${key}`,
      mergeSettings(defaultSettings, settings),
      key
    );
    setGetData(data);
    return data;
  }, []);

  const getByName = useCallback(async (name, settings = {}) => {
    const data = await detailGetData(
      `${URL_BASE}name/${name}`,
      mergeSettings(defaultSettings, settings),
      name
    );
    setGetData(data);
    return data;
  }, []);

  const create = useCallback(async (payload, settings = {}) => {
    return await putData(`${URL_BASE}`, {
      ...mergeSettings(defaultSettings, settings),
      body: payload
    });

    //const currentPageDateNew = currentPageData;
    //currentPageDateNew.unshift(data.id)
    //setCurrentPageData(currentPageDateNew)
  }, []);

  //todo patch
  const update = useCallback(async (id, payload, settings = {}) => {
    return await patchData(
      `${URL_BASE}${id}`,
      { ...mergeSettings(defaultSettings, settings), body: payload },
      id
    );
  }, []);

  const disable = useCallback(async (id, settings = {}) => {
    return await disableData(
      `${URL_BASE}${id}/disable`,
      mergeSettings(defaultSettings, settings),
      id
    );
  }, []);

  const enable = useCallback(async (id, settings = {}) => {
    return await enableData(
      `${URL_BASE}${id}/enable`,
      mergeSettings(defaultSettings, settings),
      id
    );
  }, []);

  const delete_ = useCallback(async (id, settings = {}) => {
    return await deleteData(
      `${URL_BASE}${id}`,
      mergeSettings(defaultSettings, settings),
      id
    );
  }, []);

  const reset_ = useCallback(async (id, settings = {}) => {
    setCurrentPageData([]);
  }, []);

  const entity = useMemo(() => capitalizeFirstLetter(entityName), [entityName]);

  return {
    error: {
      fetchingError,
      gettingError,
      patchingError,
      puttingError,
      deletingError,
      disablingError,
      enablingError
    },
    loading: {
      fetching,
      getting,
      patching,
      putting,
      deleting,
      disabling,
      enabling
    },
    actions: {
      [`list${entity}${entity.substr(entity.length - 1) === "s" ? "" : "s"
        }`]: list,
      [`get${entity}`]: get,
      [`get${entity}ByKey`]: getByKey,
      [`get${entity}ByName`]: getByName,
      [`create${entity}`]: create,
      [`update${entity}`]: update,
      [`disable${entity}`]: disable,
      [`enable${entity}`]: enable,
      [`delete${entity}`]: delete_,
      [`reset${entity}`]: reset_
    },
    list: currentPageData.map(id => data[id]).filter(item => item),
    data: getData,
    pagination: { currentPage, rowCount, pageCount, pageSize }
  };
};

export default hook;
